import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";
import { Leaf, ArrowLeft } from "lucide-react";

export default function AdminLogin() {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    const cleaned = (email || "").trim().toLowerCase();
    if (!cleaned) {
      return toast.error("Please enter your email.");
    }
    // simple email shape check (permissive — trims common paste noise)
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(cleaned)) {
      return toast.error("That doesn't look like a valid email address.");
    }
    setLoading(true);
    try {
      await login(cleaned, password);
      toast.success("Welcome back, organizer.");
      navigate("/admin");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#fdfbf7] flex items-center justify-center px-6">
      <div className="w-full max-w-md">
        <Link
          to="/"
          className="inline-flex items-center gap-1 text-sm text-stone-500 hover:text-[#1a2947] mb-8"
          data-testid="back-home-link"
        >
          <ArrowLeft className="h-4 w-4" /> Back home
        </Link>
        <div className="bg-white border border-[#e6e2d6] rounded-2xl p-8 shadow-[0_20px_60px_-30px_rgba(44,42,38,0.25)]">
          <div className="flex items-center gap-2 mb-6">
            <img src="/bailey-logo.png" alt="Bailey Family Reunion" className="h-10 w-10 rounded-full object-cover ring-2 ring-[#1a2947]/10" />
            <span className="font-serif text-2xl text-stone-900">Bailey Family Reunion</span>
          </div>
          <div className="label-eyebrow mb-2">Organizer Sign In</div>
          <h1 className="font-serif text-3xl text-stone-900 mb-8">
            Welcome back.
          </h1>
          <form onSubmit={submit} noValidate className="space-y-5">
            <div>
              <Label htmlFor="email" className="label-eyebrow">
                Email
              </Label>
              <Input
                id="email"
                type="text"
                inputMode="email"
                autoComplete="email"
                autoCapitalize="none"
                spellCheck={false}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="organizer@family.com"
                className="mt-2 bg-stone-50 border-stone-200"
                data-testid="admin-email-input"
              />
            </div>
            <div>
              <Label htmlFor="password" className="label-eyebrow">
                Password
              </Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="mt-2 bg-stone-50 border-stone-200"
                data-testid="admin-password-input"
              />
            </div>
            <Button
              type="submit"
              disabled={loading}
              className="w-full rounded-full bg-[#1a2947] hover:bg-[#0f1a2e] btn-pill h-11"
              data-testid="admin-login-submit"
            >
              {loading ? "Signing in..." : "Sign in"}
            </Button>
            <div className="text-center">
              <Link
                to="/admin/forgot-password"
                className="text-sm text-stone-500 hover:text-[#1a2947]"
                data-testid="forgot-password-link"
              >
                Forgot your password?
              </Link>
            </div>
          </form>
        </div>
        <p className="text-xs text-stone-500 text-center mt-6">
          For family members, use the private link your organizer sent you.
        </p>
      </div>
    </div>
  );
}
